import { useState, useEffect, ReactElement } from "react";
import theme from "@/theme/theme";
import {
  ColoredAlpineIcon,
  ColoredBackpackIcon,
  ColoredCactusIcon,
  ColoredCampIcon,
  ColoredCheersIcon,
  ColoredDeerIcon,
  ColoredFarmIcon,
  ColoredFerrisIcon,
  ColoredGuitarIcon,
  ColoredKayakIcon,
  ColoredPalmIcon,
  ColoredParachuteIcon,
  ColoredParkIcon,
  ColoredScubaMaskIcon,
  ColoredSignpostIcon,
  ColoredSunbedIcon,
  ColoredTelescopeIcon,
  ColoredTreesIcon,
  ColoredTropicalIcon,
  ColoredVolcanoIcon,
} from "@/components/Icon";
import styles from "./CategoryFilter.module.scss";

const useCategory = (categoryName: string | undefined, isSelected = false) => {
  const [category, setCategory] = useState<
    { title: string; value: string; icon: ReactElement } | undefined
  >(undefined);

  useEffect(() => {
    const categoryIcon = [
      {
        title: "Tropical & Beachside",
        value: "tropical_and_beachside",
        icon: (
          <ColoredTropicalIcon
            className={styles.cardIcon}
            variant={isSelected ? "contained" : "outlined"}
          />
        ),
      },
      {
        title: "Cold Climate & Alpine",
        value: "cold_climate_and_alpine",
        icon: (
          <ColoredAlpineIcon
            className={styles.strokeIcon}
            variant={isSelected ? "contained" : "outlined"}
            stroke={theme.palette.common.grey[200]}
          />
        ),
      },
      {
        title: "Outback & Remote",
        value: "outback_and_remote",
        icon: (
          <ColoredCampIcon
            className={styles.strokeIcon}
            stroke={theme.palette.common.grey[200]}
            variant={isSelected ? "contained" : "outlined"}
          />
        ),
      },
      {
        title: "Rainforest & Eco Escape",
        value: "rainforest_and_eco_escape",
        icon: (
          <ColoredTreesIcon
            className={styles.strokeIcon}
            variant={isSelected ? "contained" : "outlined"}
            stroke={theme.palette.common.grey[200]}
          />
        ),
      },
      {
        title: "Countryside & Rural Escape",
        value: "countryside_and_rural_escape",
        icon: (
          <ColoredFarmIcon
            className={styles.cardIcon}
            variant={isSelected ? "contained" : "outlined"}
          />
        ),
      },
      {
        title: "Desert & Arid Adventure",
        value: "desert_and_arid_adventure",
        icon: (
          <ColoredCactusIcon
            className={styles.cardIcon}
            variant={isSelected ? "contained" : "outlined"}
          />
        ),
      },
      {
        title: "City Buzz",
        value: "city_buzz",
        icon: (
          <ColoredFerrisIcon
            className={styles.cardIcon}
            variant={isSelected ? "contained" : "outlined"}
          />
        ),
      },
      {
        title: "Island Life",
        value: "island_life",
        icon: (
          <ColoredPalmIcon
            className={styles.cardIcon}
            variant={isSelected ? "contained" : "outlined"}
          />
        ),
      },

      {
        title: "Backpacker Social Scene",
        value: "backpacker_social_scene",
        icon: (
          <ColoredBackpackIcon
            className={styles.cardIcon}
            variant={isSelected ? "contained" : "outlined"}
          />
        ),
      },
      {
        title: "Laidback & Slow Living",
        value: "laidback_and_slow_living",
        icon: (
          <ColoredSunbedIcon
            className={styles.strokeIcon}
            variant={isSelected ? "contained" : "outlined"}
            stroke={theme.palette.common.grey[200]}
          />
        ),
      },
      {
        title: "Adrenaline & Extreme Sports",
        value: "adrenaline_and_extreme_sports",
        icon: (
          <ColoredParachuteIcon
            className={styles.cardIcon}
            variant={isSelected ? "contained" : "outlined"}
            viewBox="0 0 24 24"
          />
        ),
      },
      {
        title: "Foodie & Wine Regions",
        value: "foodie_and_wine_regions",
        icon: (
          <ColoredCheersIcon
            className={styles.cardIcon}
            variant={isSelected ? "contained" : "outlined"}
          />
        ),
      },
      {
        title: "Surf & Beach Culture",
        value: "surf_and_beach_culture",
        icon: (
          <ColoredKayakIcon
            className={styles.strokeIcon}
            variant={isSelected ? "contained" : "outlined"}
            stroke={theme.palette.common.grey[200]}
          />
        ),
      },
      {
        title: "Hiking & National Park",
        value: "hiking_and_national_parks",
        icon: (
          <ColoredParkIcon
            className={styles.cardIcon}
            variant={isSelected ? "contained" : "outlined"}
            viewBox="0 0 24 24"
          />
        ),
      },
      {
        title: "Music & Art Hubs",
        value: "music_and_arts_hubs",
        icon: (
          <ColoredGuitarIcon
            className={styles.cardIcon}
            variant={isSelected ? "contained" : "outlined"}
          />
        ),
      },
      {
        title: "Wildlife & Conservation",
        value: "wildlife_and_conservation",
        icon: (
          <ColoredDeerIcon
            className={styles.strokeIcon}
            variant={isSelected ? "contained" : "outlined"}
            stroke={theme.palette.common.grey[200]}
          />
        ),
      },
      {
        title: "Diving & Marine Adventures",
        value: "diving_and_marine_adventures",
        icon: (
          <ColoredScubaMaskIcon
            className={styles.strokeIcon}
            stroke={theme.palette.common.grey[200]}
            variant={isSelected ? "contained" : "outlined"}
          />
        ),
      },
      {
        title: "Cultural & Indigenous Experiences",
        value: "cultural_and_indigenous_experiences",
        icon: (
          <ColoredSignpostIcon
            className={styles.cardIcon}
            variant={isSelected ? "contained" : "outlined"}
            viewBox="0 0 24 24"
          />
        ),
      },
      {
        title: "Stargazing & Remote Tranquility",
        value: "stargazing_and_remote_tranquility",
        icon: (
          <ColoredTelescopeIcon
            className={styles.strokeIcon}
            variant={isSelected ? "contained" : "outlined"}
            stroke={theme.palette.common.grey[200]}
          />
        ),
      },
      {
        title: "Volcanic & Geothermal Wonders",
        value: "volcanic_and_geothermal_wonders",
        icon: (
          <ColoredVolcanoIcon
            className={styles.cardIcon}
            variant={isSelected ? "contained" : "outlined"}
          />
        ),
      },
    ];
    const currentCategory = categoryIcon.find(
      (each) => each.value === categoryName
    );
    setCategory(currentCategory);
  }, [categoryName, isSelected]);

  return category;
};

export default useCategory;
